/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
import {
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Check,
  Clock,
  CheckCircle2,
  HandCoins,
  Pencil,
  XCircle,
  Paperclip,
  Trash2,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ParentBadge } from "@/components/ParentBadge";
import { ReceiptStatusBadge, deriveReceiptStatus } from "@/components/ReceiptStatusBadge";
import { ComplianceChip } from "@/components/expenses/ComplianceChip";
import { formatDate, money } from "@/lib/format";
import { API, getToken } from "@/lib/api";

/**
 * ExpenseRow
 *
 * One row inside the Expenses page list — header bar (status / category
 * / date / payer / share rollup / outstanding badge / pending payments
 * badge / paid badge / voided meta), action button cluster (Record
 * payment / Record received / Mark settled / Confirm receipt / Edit /
 * Void / Restore), and the expanded detail panel (breakdown card,
 * receipts list, payment log, edit history).
 *
 * Extracted from pages/Expenses.jsx in the v77.99.19 Phase 3 frontend
 * decomposition pass (slice 2). Behaviour, copy, data-testids, and
 * styling are preserved verbatim.
 *
 * Props use grouped `ctx` (read-only context for derived rendering)
 * and `actions` (event callbacks) to keep the prop surface tidy.
 *
 *   ctx = {
 *     user, members, children, currency,
 *     STATUS_META, catMeta, PM_LABEL, summariseEdit,
 *     splitLabel, firstName, nameOf, eligibleParentsFor,
 *   }
 *   actions = {
 *     toggleExpand(id),
 *     openAttach(x), openReceipt(x), openPay(x), openConfirm(x, p),
 *     openEdit(x), openVoid(x),
 *     setNotRequired(x, flag),
 *     quickSettle(x, iAmPayer),
 *     unvoid(x),
 *     removePayment(x, paymentId),
 *     detachReceipt(x, receiptId),
 *   }
 */
export function ExpenseRow({ x, isExpanded, ctx, actions }) {
  const {
    user, members, children, currency,
    STATUS_META, catMeta, PM_LABEL, summariseEdit,
    splitLabel, firstName, nameOf, eligibleParentsFor,
    unallocatedCredit = 0,
  } = ctx;
  const br = x.breakdown || {};
  const status = x.status || br.status || "outstanding";
  const meta = STATUS_META[status] || STATUS_META.outstanding;
  const mine = x.created_by === user?.id;
  const payer = x.paid_by;
  const iAmPayer = user?.id && user.id === payer;
  const iAmDebtor = user?.id && user.id !== payer;
  const cat = catMeta(x.category || "other");
  const pendingPayments = (br.payments || []).filter((p) => !p.confirmed && !p.disputed);
  const myPending = pendingPayments.find((p) => p.recorded_by === user?.id);
  const payerPending = pendingPayments[0]; // first awaiting confirmation
  // v77.99.113 — Credit-coverage derivation. When the sum of credit
  // allocations on this expense is >= the gross outstanding, the
  // expense is "effectively settled" — even though the raw status
  // stays "outstanding" (we never flip the doc to preserve the
  // audit trail). The chip surfaces this to the user; the
  // action-button gates skip Record Payment / Mark Settled / Record
  // Payment Received because they'd duplicate the credit settlement.
  const _credAllocs = x.credit_allocations || [];
  const _credSum = _credAllocs.reduce((s, a) => s + (a.amount || 0), 0);
  const _credCoveredFully = _credSum > 0 && (br.outstanding_to_payer || 0) - _credSum <= 0.005;
  const _credAllAuto = _credCoveredFully && _credAllocs.every((a) => a.auto);

  return (
    <div key={x.id} className={`border bg-white rounded-md overflow-hidden relative ${x.voided ? "border-[#E4E4E4] opacity-75" : "border-[#D9E1E3]"}`} style={{ borderLeft: `4px solid ${x.voided ? "#E4E4E4" : cat.bar}` }} data-testid={`expense-row-${x.id}`}>
      <div className="p-4 flex items-start gap-3 flex-wrap">
        <button type="button" onClick={() => actions.toggleExpand(x.id)} className="mt-0.5 text-[#6F828A] hover:text-[#2E3A3F] flex-shrink-0" data-testid={`toggle-expense-${x.id}`} aria-label={isExpanded ? "Collapse" : "Expand"}>
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {/* v77.99.106 (iter 122.m3) — Tap-anywhere-to-expand. The
            click target wraps the full content column so users on
            mobile don't have to hit the tiny chevron exactly. Uses
            role+tabIndex (not <button>) so inner clickable elements
            don't violate the nested-interactive HTML rule. */}
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            // Don't expand if the user tapped an inner button / link.
            if (e.target.closest("button, a, [role=button]") !== e.currentTarget) return;
            actions.toggleExpand(x.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              actions.toggleExpand(x.id);
            }
          }}
          className="flex-1 min-w-0 cursor-pointer rounded-sm"
          data-testid={`expand-expense-body-${x.id}`}
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${meta.className}`} data-testid={`status-${x.id}`}>{meta.label}</span>
            {/* v77.99.113 — Credit-covered chip on the status row.
                Sits beside the status pill so a parent scanning the
                list can instantly see which rows were settled by a
                lump-sum credit rather than direct payment. */}
            {!x.voided && _credCoveredFully && (
              <span
                className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm bg-[#D6F0EF] border-[#A7D8DA] text-[#0F5658] inline-flex items-center gap-1"
                data-testid={`credit-covered-${x.id}`}
                title={_credAllAuto
                  ? "Settled by auto-allocated credit"
                  : "Settled by credit allocation"}
              >
                <HandCoins className="w-2.5 h-2.5" strokeWidth={2} />
                Credit covered{_credAllAuto ? " (auto)" : ""}
              </span>
            )}
            <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm capitalize ${cat.chip}`} data-testid={`category-${x.id}`}>{x.category}</span>
            {!x.voided && x.compliance_snapshot && (
              <ComplianceChip snapshot={x.compliance_snapshot} />
            )}
            <span className="text-xs text-[#6F828A] font-mono-data">{formatDate(x.date)} · split {splitLabel(x)}</span>
          </div>
          <div className="mt-1.5 font-medium text-[#2E3A3F]">{x.title}</div>
          {/* v77.99.106 (iter 122.m3) — Payer breakdown slot.
              This div is the single source of "who paid what / who
              owes whom" copy for the row at rest. The M5 conversational
              copy pass will swap the inner JSX for a one-sentence
              natural-language summary like:
                  "You paid €120. Sam owes you €60."
              The surrounding row layout (chevron + content column +
              amount + status pill + collapsed actions) stays
              identical, so the swap is a drop-in change. Do NOT
              restructure this slot — only replace the inner copy. */}
          <div
            className="mt-1 text-sm text-[#6F828A]"
            data-testid={`expense-row-breakdown-${x.id}`}
            data-slot="payer-breakdown"
          >
            {iAmPayer ? (
              <>
                <ParentBadge userId={payer} members={members} className="text-sm align-baseline" testId={`expense-payer-${x.id}`} /> <span className="text-[#2E3A3F]">(you)</span> paid <span className="font-mono-data text-[#2E3A3F] font-semibold">{money(x.amount, currency)}</span> upfront.
              </>
            ) : (
              <>
                <ParentBadge userId={payer} members={members} className="text-sm align-baseline" testId={`expense-payer-${x.id}`} /> paid <span className="font-mono-data text-[#2E3A3F] font-semibold">{money(x.amount, currency)}</span> upfront.
              </>
            )}
            {!x.voided && br.other_owed_total !== undefined && (() => {
              const nonPayerIds = Object.keys(br.shares || {}).filter((id) => id !== payer && (br.shares[id] || 0) > 0);
              const soleOtherIsYou = iAmDebtor && nonPayerIds.length === 1 && nonPayerIds[0] === user?.id;
              return (
                <>
                  {" "}{iAmPayer ? "Your share" : `${firstName(payer)}'s share`} <span className="font-mono-data">{money(br.shares?.[payer] || 0, currency)}</span>
                  {" · "}{soleOtherIsYou ? "Your share" : "Other share(s)"} <span className="font-mono-data">{money(br.other_owed_total, currency)}</span>
                </>
              );
            })()}
          </div>
          {(x.all_children || (x.child_ids && x.child_ids.length > 0)) && children.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`expense-children-${x.id}`}>
              {x.all_children ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm text-[11px] text-[#2E3A3F]">
                  <span className="w-2 h-2 rounded-full bg-[#1F9FA4]"></span> All children
                </span>
              ) : (
                (x.child_ids || []).map((cid) => {
                  const c = children.find((k) => k.id === cid);
                  if (!c) return null;
                  return (
                    <span key={cid} className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#D9E1E3] bg-white rounded-sm text-[11px] text-[#2E3A3F]">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }}></span>
                      {c.name}
                    </span>
                  );
                })
              )}
            </div>
          )}
          {/* v77.4 — Unified ReceiptStatusBadge. */}
          {!x.voided && (() => {
            const r = deriveReceiptStatus(x);
            return (
              <ReceiptStatusBadge
                status={r.status}
                count={r.count}
                testid={`receipt-badge-${x.id}`}
                onView={r.status === "attached" ? () => actions.toggleExpand(x.id, true) : undefined}
                onAdd={mine && r.status === "missing" ? () => actions.openAttach(x) : undefined}
                onMarkNotNeeded={mine && r.status === "missing" ? () => actions.setNotRequired(x, true) : undefined}
                onUndoNotNeeded={mine && r.status === "not_required" ? () => actions.setNotRequired(x, false) : undefined}
              />
            );
          })()}
          {/* v77.99.114 — Hide the "X owes Y" line when the expense is
              already fully covered by a payment-credit allocation. The
              dedicated "Credit covered" chip above already communicates
              the settled state; rendering both was cosmetically
              misleading (testing agent flagged it iter114). */}
          {!x.voided && status !== "paid" && br.outstanding_to_payer > 0 && !_credCoveredFully && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm bg-[#FBEFED] border border-[#E8B8B2] text-[#8C2A22] px-2 py-1 rounded-sm max-w-full" data-testid={`owed-${x.id}`}>
              <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
              {status === "partial" ? (
                <>
                  <span className="font-semibold">Partially settled · </span>
                  <span className="font-mono-data">{money(br.paid_back_total, currency)}</span> of <span className="font-mono-data">{money(br.other_owed_total, currency)}</span> confirmed ·{" "}
                  <span className="font-mono-data font-semibold">{money(br.outstanding_to_payer, currency)}</span> remaining
                </>
              ) : (
                <>
                  {(() => {
                    const eligible = eligibleParentsFor(x.child_ids || []);
                    const debtors = eligible.filter((m) => m.id !== payer);
                    const youAreDebtor = debtors.some((m) => m.id === user?.id);
                    const debtorLabel = youAreDebtor
                      ? (debtors.length === 1
                          ? "You"
                          : `You${debtors.length > 1 ? ` and ${debtors.filter((m) => m.id !== user?.id).map((m) => m.name).join(", ")}` : ""}`)
                      : debtors.map((m) => m.name).join(", ");
                    const verb = youAreDebtor && debtors.length === 1 ? "owe" : "owes";
                    const payerLabel = iAmPayer ? "you" : nameOf(payer);
                    return (
                      <>
                        {debtorLabel} {verb} {payerLabel}{" "}
                        <span className="font-mono-data font-semibold">{money(br.outstanding_to_payer, currency)}</span>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
          {!x.voided && pendingPayments.length > 0 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm bg-[#E3EAF3] border border-[#B8C7DD] text-[#2E4A73] px-2 py-1 rounded-sm max-w-full" data-testid={`pending-${x.id}`}>
              <Clock className="w-3.5 h-3.5 flex-shrink-0" /> {pendingPayments.length} payment{pendingPayments.length === 1 ? "" : "s"} awaiting confirmation (<span className="font-mono-data">{money(pendingPayments.reduce((s, p) => s + p.amount, 0), currency)}</span>)
            </div>
          )}
          {/* v77.99.109 — Credit-applied summary pill (collapsed
              view). Shows the running total of all credit
              allocations applied to this expense. Tap the row to
              expand and see the per-allocation Remove buttons. */}
          {!x.voided && (x.credit_allocations || []).length > 0 && (() => {
            const allocs = x.credit_allocations || [];
            const total = allocs.reduce((s, a) => s + (a.amount || 0), 0);
            const hasAuto = allocs.some((a) => a.auto);
            return (
              <div
                className="mt-1.5 inline-flex flex-wrap items-center gap-1.5 text-xs bg-[#D6F0EF] border border-[#A7D8DA] text-[#0F5658] px-2 py-1 rounded-sm"
                data-testid={`credit-applied-${x.id}`}
              >
                <HandCoins className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span>
                  Credit applied: <span className="font-mono-data font-semibold">{money(total, currency)}</span>
                  {allocs.length > 1 && <span> · {allocs.length} allocations</span>}
                  {hasAuto && <span className="ml-1 text-[10px] uppercase tracking-[0.06em] text-[#0F5658]/80">· auto</span>}
                </span>
              </div>
            );
          })()}
          {/* v77.99.106 — Credit-available discoverability pill. Only
              shown when the current user is a debtor on this expense,
              the expense still has outstanding balance, AND there's
              unallocated payment-credit balance in the household.
              Click → switches to the Payments tab and auto-opens the
              AllocateDialog targeted at this expense.
              Hidden for voided + paid expenses (already by the
              outer status guard further up). */}
          {!x.voided && status !== "paid"
            && iAmDebtor
            && br.outstanding_to_payer > 0
            && unallocatedCredit > 0.005
            && !_credCoveredFully
            && actions.applyCreditToExpense && (
            <button
              type="button"
              onClick={() => actions.applyCreditToExpense(x)}
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs bg-[#D6F0EF] border border-[#A7D8DA] text-[#0F5658] hover:bg-[#BFE4E3] px-2 py-1 rounded-sm transition-colors"
              data-testid={`apply-credit-${x.id}`}
              title="Use unallocated payment credit to settle this expense"
            >
              <Wallet className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span>
                <span className="font-mono-data font-semibold">{money(unallocatedCredit, currency)}</span> credit available · Apply
              </span>
            </button>
          )}
          {!x.voided && status === "paid" && br.other_owed_total > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#116B6E]" data-testid={`paid-${x.id}`}>
              <span className="font-mono-data">{money(br.paid_back_total || br.other_owed_total, currency)}</span> reimbursed to {iAmPayer ? "you" : nameOf(payer)}
            </div>
          )}
          {x.voided && (
            <div className="mt-1.5 text-xs text-[#8A8A8A]" data-testid={`voided-${x.id}`}>
              Voided by {x.voided_by_name || "—"} · {x.void_reason}
            </div>
          )}
          {x.settlement_note && (
            <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[#116B6E]">Cleared via settlement</div>
          )}
        </div>
        {/* v77.99.106 (iter 122.m3) — Banking-style resting state.
            Actions are hidden until the user taps to expand the row.
            Keeps the list scannable: amount + status + who-owes-whom
            dominant at rest, full action surface revealed on demand. */}
        {isExpanded && (
        <div
          className="flex flex-wrap items-center gap-2 sm:justify-end basis-full sm:basis-auto sm:w-auto pt-3 sm:pt-0 border-t border-[#EEF3F4] sm:border-t-0 -mx-4 sm:mx-0 px-4 sm:px-0"
          data-testid={`expense-actions-${x.id}`}
        >
          {/* v77.99.113 — Hide Record Payment / Record Payment
              Received / Mark Settled when the expense is already
              fully covered by credit allocations. The user can still
              reach the deallocate flow via the Credit allocations
              card in the expanded detail if they need to reverse. */}
          {!x.voided && iAmDebtor && br.outstanding_to_payer > 0.005 && !myPending && !_credCoveredFully && (
            <Button size="sm" onClick={() => actions.openPay(x)} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`record-payment-${x.id}`}>Record payment</Button>
          )}
          {!x.voided && iAmPayer && br.outstanding_to_payer > 0.005 && !_credCoveredFully && (
            <Button size="sm" onClick={() => actions.openReceipt(x)} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[180px] sm:min-w-0" data-testid={`record-receipt-${x.id}`}>
              <HandCoins className="w-3 h-3 mr-1" /> Record payment received
            </Button>
          )}
          {!x.voided && br.outstanding_to_payer > 0.005 && !_credCoveredFully && (iAmPayer || (iAmDebtor && !myPending)) && (
            <Button size="sm" variant="outline" onClick={() => actions.quickSettle(x, !!iAmPayer)} className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#D6F0EF] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`mark-settled-${x.id}`} title="Log the full remaining amount in one click">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark fully settled
            </Button>
          )}
          {!x.voided && iAmPayer && payerPending && (
            <Button size="sm" onClick={() => actions.openConfirm(x, payerPending)} className="bg-[#4A6FA5] hover:bg-[#3A5A8A] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`confirm-payment-${x.id}`}>
              <Check className="w-3 h-3 mr-1" /> Confirm receipt
            </Button>
          )}
          {!x.voided && mine && (
            <>
              <Button size="sm" variant="outline" onClick={() => actions.openEdit(x)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[80px] sm:min-w-0" data-testid={`edit-expense-${x.id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => actions.openVoid(x)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-7 text-[#6F828A] hover:text-[#C0291D] flex-1 sm:flex-initial min-w-[80px] sm:min-w-0" data-testid={`void-expense-${x.id}`}>
                <XCircle className="w-3 h-3 mr-1" /> Void
              </Button>
            </>
          )}
          {x.voided && mine && (
            <Button size="sm" variant="outline" onClick={() => actions.unvoid(x)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-7 flex-1 sm:flex-initial min-w-[80px] sm:min-w-0" data-testid={`unvoid-expense-${x.id}`}>Restore</Button>
          )}
        </div>
        )}
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t border-[#D9E1E3] bg-[#FAFBFB] p-4 space-y-4" data-testid={`expense-detail-${x.id}`}>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="border border-[#D9E1E3] bg-white rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Breakdown</div>
              <div className="space-y-1 font-mono-data text-sm">
                <div className="flex justify-between"><span>Total expense</span><span className="tabular-nums">{money(x.amount, currency)}</span></div>
                <div className="flex justify-between"><span>Paid upfront by</span><span>{iAmPayer ? "You" : nameOf(payer)}</span></div>
                {eligibleParentsFor(x.child_ids || []).map((m) => (
                  <div key={m.id} className="flex justify-between">
                    <span>{m.id === user?.id ? "Your share" : `${m.name}'s share`}</span>
                    <span className="tabular-nums">{money(br.shares?.[m.id] || 0, currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-1 border-t border-[#D9E1E3] mt-1">
                  <span>Owed to {iAmPayer ? "you" : firstName(payer)}</span>
                  <span className={`tabular-nums font-semibold ${br.outstanding_to_payer > 0 ? "text-[#C0291D]" : "text-[#116B6E]"}`}>{money(br.outstanding_to_payer || 0, currency)}</span>
                </div>
                {br.paid_back_total > 0 && (
                  <div className="flex justify-between text-[#6F828A]">
                    <span>Already reimbursed</span>
                    <span className="tabular-nums">{money(br.paid_back_total, currency)}</span>
                  </div>
                )}
              </div>
              {x.notes && (
                <div className="mt-3 pt-3 border-t border-[#D9E1E3] text-[#6F828A] whitespace-pre-wrap">{x.notes}</div>
              )}
              {/* Receipt list */}
              {(() => {
                const r = deriveReceiptStatus(x);
                const rids = r.ids;
                const rmeta = (x.receipts_meta || []).reduce((m, rr) => { m[rr.id] = rr; return m; }, {});
                return (
                  <div className="mt-3 pt-3 border-t border-[#D9E1E3]" data-testid={`receipts-detail-${x.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                        Receipts {rids.length > 0 && <span className="text-[#2E3A3F] font-medium">· {rids.length}</span>}
                      </div>
                      {!x.voided && mine && (
                        <div className="flex items-center gap-3">
                          {r.status === "missing" && (
                            <button
                              type="button"
                              onClick={() => actions.setNotRequired(x, true)}
                              className="inline-flex items-center gap-1 text-[11px] text-[#2E5044] hover:underline"
                              data-testid={`receipt-mark-not-needed-detail-${x.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Mark as not needed
                            </button>
                          )}
                          {r.status === "not_required" && (
                            <button
                              type="button"
                              onClick={() => actions.setNotRequired(x, false)}
                              className="inline-flex items-center gap-1 text-[11px] text-[#7A5A1E] hover:underline"
                              data-testid={`receipt-undo-not-needed-detail-${x.id}`}
                            >
                              Undo "not needed"
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => actions.openAttach(x)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#1F9FA4] hover:underline"
                            data-testid={`receipt-add-detail-${x.id}`}
                          >
                            <Paperclip className="w-3 h-3" /> Add receipt
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-[#6F828A]">
                      {r.status === "attached" && (rids.length === 1 ? "1 receipt attached." : `${rids.length} receipts attached.`)}
                      {r.status === "not_required" && (
                        <span className="inline-flex items-center gap-1 text-[#2E5044]">
                          <CheckCircle2 className="w-3 h-3" /> Marked as not needing a receipt.
                        </span>
                      )}
                      {r.status === "missing" && "No receipts attached yet."}
                    </div>
                    {rids.length > 0 && (
                      <ul className="mt-1.5 space-y-1" data-testid={`receipts-list-${x.id}`}>
                        {rids.map((rid) => {
                          const m = rmeta[rid];
                          const label = m?.original_filename || `Receipt ${rid.slice(0, 8)}`;
                          return (
                            <li key={rid} className="flex items-center gap-2 text-xs">
                              <a
                                href={`${API}/files/${rid}/download`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[#1F9FA4] hover:underline truncate"
                                data-testid={`receipt-link-${x.id}-${rid}`}
                              >
                                <Paperclip className="w-3 h-3 shrink-0" />
                                <span className="truncate">{label}</span>
                              </a>
                              {!x.voided && mine && (
                                <button
                                  type="button"
                                  onClick={() => actions.detachReceipt(x, rid)}
                                  className="ml-auto text-[#C0291D] hover:text-[#8C1F15]"
                                  title="Remove receipt"
                                  data-testid={`receipt-remove-${x.id}-${rid}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="border border-[#D9E1E3] bg-white rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Payment log</div>
              {(br.payments || []).length === 0 ? (
                <div className="text-sm text-[#6F828A]">No payments logged yet.</div>
              ) : (
                <ul className="space-y-2 text-sm" data-testid={`payments-list-${x.id}`}>
                  {(br.payments || []).map((p) => {
                    const stateBadge = p.voided
                      ? { label: "Voided", cls: "border-[#8C2A22] bg-[#FBEFED] text-[#8C2A22]" }
                      : p.disputed
                      ? { label: "Not received", cls: "border-[#C0291D] bg-[#FBEFED] text-[#8C2A22]" }
                      : p.recorded_by_recipient
                      ? { label: "Recorded by recipient", cls: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]" }
                      : p.confirmed
                      ? { label: "Confirmed", cls: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]" }
                      : { label: "Awaiting confirmation", cls: "border-[#4A6FA5] bg-[#E3EAF3] text-[#2E4A73]" };
                    return (
                      <li key={p.id} className="flex items-start justify-between gap-2 border-b border-[#EEF1F2] pb-2 last:border-b-0 last:pb-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono-data">{money(p.amount, currency)} · {PM_LABEL[p.method] || p.method}</span>
                            <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border rounded-sm ${stateBadge.cls}`} data-testid={`payment-state-${p.id}`}>{stateBadge.label}</span>
                          </div>
                          <div className="text-xs text-[#6F828A] mt-0.5">{p.date} · logged by {p.recorded_by_name || "—"}{p.recorded_by_recipient ? " (recipient)" : ""}{p.reference ? ` · ref ${p.reference}` : ""}</div>
                          {p.note && <div className="text-xs text-[#6F828A] italic mt-0.5">{p.note}</div>}
                          {p.confirmed && p.confirmed_at && (
                            <div className="text-[10px] text-[#116B6E] mt-0.5">Confirmed {p.confirmed_at.slice(0, 16).replace("T", " ")}</div>
                          )}
                          {p.disputed && p.dispute_reason && (
                            <div className="text-[11px] text-[#8C2A22] italic mt-0.5">Disputed: {p.dispute_reason}</div>
                          )}
                          {p.voided && p.voided_reason && (
                            <div className="text-[11px] text-[#8C2A22] italic mt-0.5" data-testid={`payment-voided-reason-${p.id}`}>Voided: {p.voided_reason}</div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {iAmPayer && !p.confirmed && !p.disputed && (
                            <Button size="sm" onClick={() => actions.openConfirm(x, p)} className="bg-[#4A6FA5] hover:bg-[#3A5A8A] rounded-sm h-7" data-testid={`confirm-row-${p.id}`}>
                              <Check className="w-3 h-3 mr-1" /> Review
                            </Button>
                          )}
                          {/* v77.99.115g — Customer-facing "Reverse duplicate".
                              Shown only on a CONFIRMED, non-voided, non-disputed
                              direct payment when the SAME expense also has a
                              credit allocation (the double-count signature). The
                              backend enforces the same overlap rule, so this is
                              UX guard-rail. Available to the upfront payer (iAmPayer)
                              who can legitimately verify the duplication. */}
                          {iAmPayer && p.confirmed && !p.disputed && !p.voided && _credSum > 0.005 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => actions.reverseDuplicate(x, p.id)}
                              className="border-[#C0291D] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm h-7 text-[11px]"
                              data-testid={`reverse-duplicate-${p.id}`}
                              title="This expense is already covered by an allocated credit — this payment is a duplicate. Click to void it."
                            >
                              Reverse — covered by credit
                            </Button>
                          )}
                          {iAmDebtor && p.recorded_by === user?.id && !p.confirmed && !p.disputed && !x.voided && (
                            <button type="button" onClick={() => actions.removePayment(x, p.id)} className="text-[#6F828A] hover:text-[#C0291D] p-1" title="Remove unconfirmed payment" data-testid={`remove-payment-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          {/* v77.99.109 — Credit allocations applied to this expense.
              Shown in the expanded detail so a parent can review
              what's been allocated (auto OR manual) and reverse
              individual entries. Each row carries credit_id + alloc
              id so DELETE /credits/{cid}/allocations/{aid} can be
              fired without the user navigating away. */}
          {(x.credit_allocations || []).length > 0 && (
            <div className="border border-[#D9E1E3] bg-white rounded-sm p-3" data-testid={`credit-allocations-${x.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] inline-flex items-center gap-1.5">
                  <HandCoins className="w-3 h-3" strokeWidth={2} />
                  Credit allocations
                </div>
                <div className="text-[10px] text-[#6F828A]">
                  {x.credit_allocations.length} entr{x.credit_allocations.length === 1 ? "y" : "ies"}
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                {x.credit_allocations.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-start justify-between gap-2 border-b border-[#EEF1F2] pb-2 last:border-b-0 last:pb-0"
                    data-testid={`credit-alloc-${a.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-data">{money(a.amount, a.currency || currency)}</span>
                        <span
                          className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border rounded-sm ${
                            a.auto
                              ? "border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658]"
                              : "border-[#D9E1E3] bg-[#F4F7F8] text-[#6F828A]"
                          }`}
                        >
                          {a.auto ? "Auto" : "Manual"}
                        </span>
                      </div>
                      <div className="text-xs text-[#6F828A] mt-0.5">
                        Credit from {a.credit_date || "—"}
                        {a.credit_note ? ` · ${a.credit_note}` : ""}
                        {a.allocated_at ? ` · allocated ${a.allocated_at.slice(0, 10)}` : ""}
                      </div>
                    </div>
                    {!x.voided && (
                      <button
                        type="button"
                        onClick={() => actions.removeAllocation(a)}
                        className="text-[#6F828A] hover:text-[#A93A2F] p-1"
                        title={a.auto
                          ? "Remove this auto-allocation (overrides the credit's auto choice for this expense)"
                          : "Remove this credit allocation"}
                        data-testid={`remove-alloc-${a.id}`}
                      >
                        <XCircle className="w-4 h-4" strokeWidth={1.8} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(x.edit_history || []).length > 0 && (
            <div className="border border-[#D9E1E3] bg-white rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Edit history</div>
              <ul className="text-xs text-[#6F828A] space-y-1.5" data-testid={`edit-history-${x.id}`}>
                {x.edit_history.map((h) => {
                  const changes = summariseEdit(h.diff);
                  const d = h.at ? new Date(h.at) : null;
                  const when = d && !isNaN(d)
                    ? `${formatDate(h.at)} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
                    : (h.at || "").slice(0, 16).replace("T", " ");
                  return (
                    <li key={h.id}>
                      <span className="text-[#2E3A3F]">Edited on {when}</span>
                      <span> by {h.by_name || "—"}</span>
                      {h.forced_financial && <span className="ml-1 text-[#B88A3A]">(financial override)</span>}
                      {changes.length > 0 && (
                        <span className="block text-[#6F828A] ml-0.5">{changes.join(" · ")} updated</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ExpenseRow;
